import { ZfBox, ZfLoadingSpinner, ZfStack, ZfTab, ZfTabList } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { useClient } from '../../app/api';
import { useWealthPlanningNavigate } from '../../app/hooks/useWealthPlanningNavigate';
import { useLiquidityOverview } from '../api';
import '../styles/main.css';
import { LiquidityCalculatorFooter } from './liquidity-calculator/liquidity-calculator-footer';
import { LiquidityPlannerTitleBar } from './liquidity-planner-title-bar';

type LiquidityPlannerTab = 'overview' | 'calculator';
const liquidityPlannerTabs: LiquidityPlannerTab[] = ['overview', 'calculator'];

interface ILiquidityPlannerProps {
    partnerUuid: string;
}

export const LiquidityPlanner: FunctionComponent<ILiquidityPlannerProps> = ({ partnerUuid }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { navigate } = useWealthPlanningNavigate();
    const { data: liquidityOverview, isLoading } = useLiquidityOverview(partnerUuid);
    const { data: client } = useClient(partnerUuid);

    const pathSegments = pathname.split('/').filter(Boolean);
    const selectedTab: LiquidityPlannerTab = pathSegments[pathSegments.length - 1] === 'calculator' ? 'calculator' : 'overview';

    const handleTabChange = (tab: LiquidityPlannerTab) => {
        navigate(`/${partnerUuid}/liquidity-planner${tab === 'calculator' ? '/calculator' : ''}`);
    };

    if (isLoading || !liquidityOverview) return <ZfLoadingSpinner style={{ margin: 'auto' }} />;

    return (
        <ZfStack direction="column" className="liquidity-planner">
            <ZfStack direction="column" spacing="extra-large" className="liquidity-planner__header">
                <LiquidityPlannerTitleBar {...liquidityOverview.metrics} currency={client?.currency ?? 'CHF'} />
                <ZfTabList value={selectedTab} onTabChange={(e) => handleTabChange(e.detail as LiquidityPlannerTab)}>
                    {liquidityPlannerTabs.map((tab) => (
                        <ZfTab key={tab} label={t(`liquidityPlannerTab.${tab}`)} value={tab} />
                    ))}
                </ZfTabList>
            </ZfStack>
            <ZfBox className="liquidity-planner__content">
                <ZfStack direction="column" className="liquidity-planner__content-box">
                    <Outlet context={{ partnerUuid }} />
                </ZfStack>
            </ZfBox>
            {selectedTab === 'calculator' && <LiquidityCalculatorFooter />}
        </ZfStack>
    );
};
